/**
 * Fire-and-forget admin product-analytics for the embedded admin UI.
 *
 * Used ONLY for the three events that have no clean server-side hook:
 *   - ns_campaign_limit_reached  (campaign create throws before its onSuccess)
 *   - ns_onboarding_step_completed
 *   - ns_theme_extension_enabled
 *
 * Everything else is emitted server-side from Gadget actions. This helper posts to
 * the authenticated /track-admin-event route, which whitelists these names and
 * enqueues the real send. It NEVER awaits and NEVER throws — analytics must not
 * block or break the merchant's admin experience.
 */

/**
 * Is the pipeline live in this environment?
 *
 * ⚠️ USE `process.env.GADGET_ENV`, NOT `import.meta.env.PROD`. Gadget's DEVELOPMENT
 * environment is still built by Vite in production MODE, so `import.meta.env.PROD` is
 * `true` there and would gate nothing. `GADGET_ENV` is the per-environment value Gadget
 * bakes in, and the Gadget Vite plugin inlines it because "GADGET_ENV" is listed in
 * VITE_PUBLIC_ENV_PREFIXES (.gadget/server/dist-esm/vite/helpers.js:216-222).
 *
 * ⚠️ The baked value tracks the VITE MODE, not the Gadget environment (helpers.js:69), so a
 * plain `vite build` always bakes "production". You therefore CANNOT verify the dev gate with
 * a default build.
 *
 * ⚠️ AND DO NOT VERIFY BY GREPPING FOR "track-admin-event" — the string is present in BOTH
 * builds, so its presence proves nothing. The escape-hatch var below is not statically known,
 * which keeps the expression non-constant in a dev build and prevents DCE. Read the emitted
 * GATE instead. Measured 2026-08-20:
 *
 *   vite build --mode development  ->  var S={}; const P=S.GADGET_PUBLIC_ADMIN_ANALYTICS==="true";
 *                                      function j(...){try{if(!P)return; i.fetch("/track-admin-event"...
 *                                      GADGET_ENV folded away; P is false; the fetch is unreachable.
 *
 *   vite build                     ->  function I(...){try{ i.fetch("/track-admin-event"...
 *                                      Gate GONE — "production"==="production" folds true, so
 *                                      `if(!true) return` is dead code and esbuild strips it.
 *                                      Zero runtime cost in prod.
 *
 * The second shape is the one that matters: it is the proof production analytics still fires.
 * Gadget's dev environment serves the Vite DEV SERVER (viteHandle.js:4-19) — no minification
 * there, so the gate is a plain runtime early-return.
 *
 * Escape hatch: GADGET_PUBLIC_ADMIN_ANALYTICS=true. Same variable name as the backend gate
 * in api/helpers/adminAnalytics.js — keep them identical, or you get a half-enabled
 * pipeline that looks like an event bug.
 */
const ADMIN_ANALYTICS_ENABLED =
  process.env.GADGET_ENV === 'production' ||
  process.env.GADGET_PUBLIC_ADMIN_ANALYTICS === 'true';

export const ADMIN_UI_EVENTS = {
  CAMPAIGN_LIMIT_REACHED: "ns_campaign_limit_reached",
  ONBOARDING_STEP_COMPLETED: "ns_onboarding_step_completed",
  THEME_EXTENSION_ENABLED: "ns_theme_extension_enabled",
  // Setup-call prompt. These three MUST also appear in FRONTEND_ALLOWED_EVENTS in
  // api/helpers/adminAnalytics.js or POST-track-admin-event rejects them with a 400 --
  // which is invisible here, because trackAdminUiEvent is fire-and-forget and fetch()
  // does not reject on a non-2xx.
  //
  // There is deliberately NO ns_setup_call_booked here: it is emitted server-side from
  // the Cal.com webhook. A browser-emittable "I booked" event would let a merchant
  // silence their own prompt without booking anything.
  SETUP_CALL_PROMPT_SHOWN: "ns_setup_call_prompt_shown",
  SETUP_CALL_PROMPT_CLICKED: "ns_setup_call_prompt_clicked",
  SETUP_CALL_PROMPT_DISMISSED: "ns_setup_call_prompt_dismissed",
};

/**
 * @param {object} api      the Gadget API client (handles auth headers)
 * @param {string} eventName  one of ADMIN_UI_EVENTS
 * @param {object} [eventParams]
 */
export function trackAdminUiEvent(api, eventName, eventParams = {}) {
  try {
    // Environment gate FIRST — stops the request at source, so the route op, the
    // enqueue op and the background-run op all disappear rather than being paid for
    // and then discarded by the credentials check inside trackAdminEvent.
    if (!ADMIN_ANALYTICS_ENABLED) return;

    // Fire-and-forget — do NOT await; swallow all failures.
    //
    // NEVER pass a `headers` object to api.fetch(). The App Bridge auth hook
    // (@gadgetinc/react-shopify-app-bridge Provider.js:50-56) rebuilds the headers
    // through `new Headers(...)` — which lower-cases every name — and then writes
    // them BACK onto the plain object with `init.headers[key] = value`. A caller's
    // "Content-Type" therefore survives alongside the added "content-type", and
    // fetch() combines the two same-name entries into the single header value
    // `application/json, application/json`. Fastify has no parser for that string,
    // so it rejects the request 415 before the route runs — invisibly, because
    // fetch() does not reject on a non-2xx. Every other api.fetch() call site in
    // web/ omits headers for this reason; the string body sends text/plain and the
    // routes JSON.parse it (see api/helpers/requestValidation.js:40).
    api
      .fetch("/track-admin-event", {
        method: "POST",
        body: JSON.stringify({ eventName, eventParams }),
      })
      .catch(() => {});
  } catch {
    // never throw from a tracking call
  }
}
